import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  deleteAutoSignature,
  deleteDoctorLogo,
  fetchDoctorById,
  updateAutoSignature,
} from "../../../redux/Thunk/Doctor/DoctorThunks";
import { useFormik } from "formik";
import Button from "../../common/Button";
import CloseBg from "../../common/icons/CloseBg";

const Profile = () => {
  const dispatch = useDispatch();
  const { doctorById, doctorDetails } = useSelector((state) => state?.doctor);

  const doctorId = doctorDetails?.doctor?.DID;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      doctorDetails: {
        DID: doctorById?.DID || "",
        contactNumber: doctorById?.contactNumber || "",
        autoSignature: doctorById?.autoSignature || false,
        signatureImage: doctorById?.signatureImage || null,
        receiptMakerAccess: doctorById?.receiptMakerAccess || false,
        autoReceiptSignature: doctorById?.autoReceiptSignature || false,
        autoStampPrescription: doctorById?.autoStampPrescription || false,
        autoStampReceipts: doctorById?.autoStampReceipts || false,
        rxCreateAccess: doctorById?.rxCreateAccess !== false,
        rxEditAccess: doctorById?.rxEditAccess !== false,
        rxListAccess: doctorById?.rxListAccess !== false,
        rcptServicesAccess: doctorById?.rcptServicesAccess !== false,
        rcptListAccess: doctorById?.rcptListAccess !== false,
        generateRxCardAccess: doctorById?.generateRxCardAccess !== false,
        rxHealthRecordAccess: doctorById?.rxHealthRecordAccess !== false,
        generateOrderAccess: doctorById?.generateOrderAccess !== false,
        doctorLogo: doctorById?.doctorLogo || null,
        useLogo: doctorById?.useLogo || false,
        useLogoInReceipt: doctorById?.useLogoInReceipt || false,
        prescriptionPrimaryColor: doctorById?.prescriptionPrimaryColor || "#E62121",
        prescriptionSecondaryColor: doctorById?.prescriptionSecondaryColor || "#8E1010",
        showRegNoInRxHeader: doctorById?.showRegNoInRxHeader !== false,
        showRegNoInReceiptHeader: doctorById?.showRegNoInReceiptHeader !== false,
        rapidScanAccess: doctorById?.rapidScanAccess !== false,
      },
      compliances: {
        gstNumber: doctorById?.compliance?.gstNumber || "",
        registrationNumber: doctorById?.compliance?.registrationNumber || "",
      },
      accountCategory: {
        category: doctorById?.accountCategory?.category || "",
      },
      personalInfo: {
        name: doctorById?.personalInfo?.name || "",
        surname: doctorById?.personalInfo?.surname || "",
        clinicName: doctorById?.personalInfo?.clinicName || "",
        tagline: doctorById?.personalInfo?.tagline || "",
        qualificationSpecialisation:
          doctorById?.personalInfo?.qualificationSpecialisation || "",
        experience: doctorById?.personalInfo?.experience || 0,
        operatorName: doctorById?.personalInfo?.operatorName || "",
        managerName: doctorById?.personalInfo?.managerName || "",
        about1: doctorById?.personalInfo?.about1 || "",
        about2: doctorById?.personalInfo?.about2 || "",
      },
      clinicAddress: {
        premisesNo: doctorById?.clinicAddress?.premisesNo || "",
        floor: doctorById?.clinicAddress?.floor || "",
        premisesName: doctorById?.clinicAddress?.premisesName || "",
        landmark: doctorById?.clinicAddress?.landmark || "",
        areaRoad: doctorById?.clinicAddress?.areaRoad || "",
        city: doctorById?.clinicAddress?.city || "",
        pinCode: doctorById?.clinicAddress?.pinCode || "",
        state: doctorById?.clinicAddress?.state || "",
        clinicContactNumber:
          doctorById?.clinicAddress?.clinicContactNumber || "",
        about: doctorById?.clinicAddress?.about || "",
      },
      emailInfo: {
        doctorEmail: doctorById?.emailInfo?.doctorEmail || "",
        clinicEmail: doctorById?.emailInfo?.clinicEmail || "",
      },
      paymentDetails: {
        bankAccountNumber: doctorById?.paymentDetail?.bankAccountNumber || "",
        accountName: doctorById?.paymentDetail?.accountName || "",
        bankName: doctorById?.paymentDetail?.bankName || "",
        ifscName: doctorById?.paymentDetail?.ifscName || "",
        upiId: doctorById?.paymentDetail?.upiId || "",
      },
    },
  });

  const handleImageUpload = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("doctorDetails.signatureImage", file);
      handleSubmit({
        signatureImage: file,
      });
    }
  };

  const handleDelete = () => {
    const confirmDelete = window.confirm("Are you sure you want to delete the signature image?");

    if (confirmDelete) {
      formik.setFieldValue("doctorDetails.signatureImage", null);
      formik.setFieldValue("doctorDetails.autoSignature", false);
      dispatch(deleteAutoSignature({ doctorId: doctorById?.DID }))
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("doctorDetails.doctorLogo", file);
      handleSubmit({
        doctorLogo: file,
      });
    }
  };

  const handleLogoDelete = () => {
    const confirmDelete = window.confirm("Are you sure you want to delete the clinic logo?");

    if (confirmDelete) {
      formik.setFieldValue("doctorDetails.doctorLogo", null);
      formik.setFieldValue("doctorDetails.useLogo", false);
      dispatch(deleteDoctorLogo({ doctorId: doctorById?.DID }))
    }
  };

  const handleOnChange = (fieldName, e) => {
    formik.setFieldValue(`doctorDetails.${fieldName}`, e.target.checked);
    handleSubmit({
      [fieldName]: e.target.checked
    })
  }

  const handleSubmit = (value) => {
    const formData = new FormData();
    if (value.autoSignature !== undefined) formData.append("autoSignature", value.autoSignature)
    if (value.signatureImage !== undefined) formData.append("signatureImage", value.signatureImage)
    if (value.receiptMakerAccess !== undefined) formData.append("receiptMakerAccess", value.receiptMakerAccess)
    if (value.autoReceiptSignature !== undefined) formData.append("autoReceiptSignature", value.autoReceiptSignature)
    if (value.autoStampPrescription !== undefined) formData.append("autoStampPrescription", value.autoStampPrescription)
    if (value.autoStampReceipts !== undefined) formData.append("autoStampReceipts", value.autoStampReceipts)
    if (value.rxCreateAccess !== undefined) formData.append("rxCreateAccess", value.rxCreateAccess)
    if (value.rxEditAccess !== undefined) formData.append("rxEditAccess", value.rxEditAccess)
    if (value.rxListAccess !== undefined) formData.append("rxListAccess", value.rxListAccess)
    if (value.generateRxCardAccess !== undefined) formData.append("generateRxCardAccess", value.generateRxCardAccess)
    if (value.rcptServicesAccess !== undefined) formData.append("rcptServicesAccess", value.rcptServicesAccess)
    if (value.rcptListAccess !== undefined) formData.append("rcptListAccess", value.rcptListAccess)
    if (value.rxHealthRecordAccess !== undefined) formData.append("rxHealthRecordAccess", value.rxHealthRecordAccess)
    if (value.generateOrderAccess !== undefined) formData.append("generateOrderAccess", value.generateOrderAccess)
    if (value.doctorLogo !== undefined) formData.append("doctorLogo", value.doctorLogo)
    if (value.useLogo !== undefined) formData.append("useLogo", value.useLogo)
    if (value.useLogoInReceipt !== undefined) formData.append("useLogoInReceipt", value.useLogoInReceipt)
    if (value.prescriptionPrimaryColor !== undefined) formData.append("prescriptionPrimaryColor", value.prescriptionPrimaryColor)
    if (value.prescriptionSecondaryColor !== undefined) formData.append("prescriptionSecondaryColor", value.prescriptionSecondaryColor)
    if (value.showRegNoInRxHeader !== undefined) formData.append("showRegNoInRxHeader", value.showRegNoInRxHeader)
    if (value.showRegNoInReceiptHeader !== undefined) formData.append("showRegNoInReceiptHeader", value.showRegNoInReceiptHeader)
    dispatch(updateAutoSignature({ doctorId: doctorById?.DID, formData }));
  }

  useEffect(() => {
    dispatch(fetchDoctorById({ doctorId: doctorId }));
  }, [dispatch, doctorId]);

  // Prescription Maker Presets
  const getRxAccessPreset = () => {
    const { rxCreateAccess, rxEditAccess, rxListAccess, generateRxCardAccess, rxHealthRecordAccess } = formik.values.doctorDetails;
    if (!rxCreateAccess && !rxEditAccess && !rxListAccess && generateRxCardAccess === false && rxHealthRecordAccess === false) return "doctor";
    if (rxCreateAccess && !rxEditAccess && !rxListAccess && generateRxCardAccess === false && rxHealthRecordAccess === false) return "reception";
    return "custom";
  };

  const handleRxAccessPreset = (preset) => {
    if (preset === "custom") return;
    let updates = {};
    if (preset === "doctor") {
      updates = { rxCreateAccess: false, rxEditAccess: false, rxListAccess: false, generateRxCardAccess: false, rxHealthRecordAccess: false };
    } else if (preset === "reception") {
      updates = { rxCreateAccess: true, rxEditAccess: false, rxListAccess: false, generateRxCardAccess: false, rxHealthRecordAccess: false };
    }
    formik.setFieldValue("doctorDetails.rxCreateAccess", updates.rxCreateAccess);
    formik.setFieldValue("doctorDetails.rxEditAccess", updates.rxEditAccess);
    formik.setFieldValue("doctorDetails.rxListAccess", updates.rxListAccess);
    formik.setFieldValue("doctorDetails.generateRxCardAccess", updates.generateRxCardAccess);
    formik.setFieldValue("doctorDetails.rxHealthRecordAccess", updates.rxHealthRecordAccess);
    handleSubmit(updates);
  };

  // Receipt Maker Presets
  const getRcptAccessPreset = () => {
    const { rcptServicesAccess, rcptListAccess } = formik.values.doctorDetails;
    if (!rcptServicesAccess && !rcptListAccess) return "doctor";
    if (rcptServicesAccess && rcptListAccess) return "reception";
    return "custom";
  };

  const handleRcptAccessPreset = (preset) => {
    if (preset === "custom") return;
    let updates = {};
    if (preset === "doctor") {
      updates = { rcptServicesAccess: false, rcptListAccess: false };
    } else if (preset === "reception") {
      updates = { rcptServicesAccess: true, rcptListAccess: true };
    }
    formik.setFieldValue("doctorDetails.rcptServicesAccess", updates.rcptServicesAccess);
    formik.setFieldValue("doctorDetails.rcptListAccess", updates.rcptListAccess);
    handleSubmit(updates);
  };

  const themes = [
    { name: "Hyperapt Red", primary: "#E62121", secondary: "#8E1010" },
    { name: "Ocean Blue", primary: "#1B3B6F", secondary: "#061A40" },
    { name: "Classic Teal", primary: "#008080", secondary: "#004D4D" },
    { name: "Forest Green", primary: "#2E7D32", secondary: "#1B5E20" },
    { name: "Steel Slate", primary: "#546E7A", secondary: "#263238" },
    { name: "Deep Plum", primary: "#6A1B9A", secondary: "#4A148C" },
    { name: "Royal Gold", primary: "#B8860B", secondary: "#5C4033" },
    { name: "Charcoal Gray", primary: "#424242", secondary: "#212121" },
    { name: "Sunrise Rose", primary: "#D81B60", secondary: "#880E4F" },
  ];

  const handleThemeSelect = (theme) => {
    formik.setFieldValue("doctorDetails.prescriptionPrimaryColor", theme.primary);
    formik.setFieldValue("doctorDetails.prescriptionSecondaryColor", theme.secondary);
    handleSubmit({
      prescriptionPrimaryColor: theme.primary,
      prescriptionSecondaryColor: theme.secondary
    });
  };

  const date = new Date(doctorById?.createdAt);

  // Format the date to MM/DD/YYYY using toLocaleDateString
  const formattedDate = date?.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return (
    <div className="bg-gray-100 py-3 px-5 rounded-[12px]">

      {/* ═══ ACCESS CONTROLS BLOCK ═══ */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* ═══ Group 1: Prescription Maker ═══ */}
          <div className="border border-primary-200 rounded-[10px] bg-white overflow-hidden">
            <div className="bg-primary-50 px-4 py-2.5 border-b border-primary-200 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM6 20V4h5v7h7v9H6z" fill="#513E3E" /></svg>
              <h3 className="text-[14px] font-semibold text-secondry-600 uppercase tracking-wide">Prescription Maker</h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Auto-Sign Prescriptions */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-[13px] font-medium text-black-lose">Smart Order: Auto-Sign Prescriptions</h4>
                  <p className="text-[11px] text-primary-400 mt-0.5">
                    {!formik.values.doctorDetails.signatureImage
                      ? "Upload signature image to enable"
                      : "Automatically signs prescriptions when generating PDF"}
                  </p>
                </div>
                {formik.values.doctorDetails.signatureImage ? (
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.autoSignature" checked={formik.values.doctorDetails.autoSignature} onChange={(e) => handleOnChange("autoSignature", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                ) : (
                  <span className="flex-shrink-0"><CloseBg fill="#8E1010" height={20} width={20} /></span>
                )}
              </div>

              {/* Auto-Stamp Prescriptions */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-[13px] font-medium text-black-lose">Auto-Stamp Prescriptions</h4>
                  <p className="text-[11px] text-primary-400">Adds clinic stamp to prescriptions when generating PDF</p>
                </div>
                <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                  <input type="checkbox" className="sr-only peer" name="doctorDetails.autoStampPrescription" checked={formik.values.doctorDetails.autoStampPrescription} onChange={(e) => handleOnChange("autoStampPrescription", e)} />
                  <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                </label>
              </div>

              {/* Show RegNo in Rx Header */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-[13px] font-medium text-black-lose">Show Registration number in header</h4>
                  <p className="text-[11px] text-primary-400 mt-0.5">Toggle visibility of registration number specifically at PDF header (does not affect stamp)</p>
                </div>
                <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                  <input type="checkbox" className="sr-only peer" name="doctorDetails.showRegNoInRxHeader" checked={formik.values.doctorDetails.showRegNoInRxHeader} onChange={(e) => handleOnChange("showRegNoInRxHeader", e)} />
                  <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                </label>
              </div>

              {/* Access Controls Divider */}
              <div className="border-t border-primary-100 pt-3">
                <p className="text-[11px] text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z" fill="#999" /></svg>
                  Access Controls (PIN-A restricted)
                </p>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Create Page</h4>
                    <p className="text-[11px] text-primary-400">Show Create tab for PIN-A users when ON</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.rxCreateAccess" checked={formik.values.doctorDetails.rxCreateAccess} onChange={(e) => handleOnChange("rxCreateAccess", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Edit Page</h4>
                    <p className="text-[11px] text-primary-400">Show Edit tab for PIN-A users when ON</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.rxEditAccess" checked={formik.values.doctorDetails.rxEditAccess} onChange={(e) => handleOnChange("rxEditAccess", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">List Page</h4>
                    <p className="text-[11px] text-primary-400">Show List tab for PIN-A users when ON</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.rxListAccess" checked={formik.values.doctorDetails.rxListAccess} onChange={(e) => handleOnChange("rxListAccess", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Health Record</h4>
                    <p className="text-[11px] text-primary-400">Show Patient Health Records block when ON</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.rxHealthRecordAccess" checked={formik.values.doctorDetails.rxHealthRecordAccess !== false} onChange={(e) => handleOnChange("rxHealthRecordAccess", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Generate Prescription Card</h4>
                    <p className="text-[11px] text-primary-400">Show &apos;Generate Prescription&apos; card when ON</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.generateRxCardAccess" checked={formik.values.doctorDetails.generateRxCardAccess !== false} onChange={(e) => handleOnChange("generateRxCardAccess", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between mb-3 border-t border-primary-50 pt-3">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Generate Order feature</h4>
                    <p className="text-[11px] text-primary-400 font-medium">Accessible to PIN-B user only when ON</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.generateOrderAccess" checked={formik.values.doctorDetails.generateOrderAccess !== false} onChange={(e) => handleOnChange("generateOrderAccess", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
                {/* Prescription Maker Preset Slider */}
                <div className="mt-4 pt-3 border-t border-primary-50">
                  <p className="text-[11px] text-primary-400 mb-2">QUICK SETUP</p>
                  <div className="flex bg-primary-100 p-1 rounded-[8px] relative overflow-hidden">
                    <div
                      className="absolute top-1 bottom-1 w-[calc(33.33%-2px)] bg-white rounded-[6px] shadow-sm transition-all duration-300 ease-in-out z-0"
                      style={{
                        transform: `translateX(${getRxAccessPreset() === 'doctor' ? '0%' : getRxAccessPreset() === 'reception' ? '100%' : '200%'})`,
                        left: getRxAccessPreset() === 'doctor' ? '4px' : getRxAccessPreset() === 'reception' ? '2px' : '0px'
                      }}
                    ></div>
                    <button type="button" onClick={() => handleRxAccessPreset('doctor')} className={`flex-1 text-center py-1.5 text-[11px] font-medium rounded-md transition-all z-10 ${getRxAccessPreset() === "doctor" ? "text-primary-700 font-semibold" : "text-primary-500 hover:text-primary-600"}`}>Doctor Operated</button>
                    <button type="button" onClick={() => handleRxAccessPreset('reception')} className={`flex-1 text-center py-1.5 text-[11px] font-medium rounded-md transition-all z-10 ${getRxAccessPreset() === "reception" ? "text-primary-700 font-semibold" : "text-primary-500 hover:text-primary-600"}`}>Reception Operated</button>
                    <button type="button" onClick={() => handleRxAccessPreset('custom')} className={`flex-1 text-center py-1.5 text-[11px] font-medium rounded-md transition-all z-10 ${getRxAccessPreset() === "custom" ? "text-primary-700 font-semibold" : "text-primary-500 hover:text-primary-600"}`}>Custom</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ Group 2: Receipt Maker ═══ */}
          <div className="border border-primary-200 rounded-[10px] bg-white overflow-hidden">
            <div className="bg-primary-50 px-4 py-2.5 border-b border-primary-200 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5zM19 19.09H5V4.91h14v14.18zM6 15h12v2H6zm0-4h12v2H6zm0-4h12v2H6z" fill="#513E3E" /></svg>
              <h3 className="text-[14px] font-semibold text-secondry-600 uppercase tracking-wide">Receipt Maker</h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Receipt Maker Access */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-[13px] font-medium text-black-lose">Receipt Maker</h4>
                  <p className="text-[11px] text-primary-400">Enable Receipt Maker module when ON</p>
                </div>
                <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                  <input type="checkbox" className="sr-only peer" name="doctorDetails.receiptMakerAccess" checked={formik.values.doctorDetails.receiptMakerAccess} onChange={(e) => handleOnChange("receiptMakerAccess", e)} />
                  <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                </label>
              </div>

              {/* Smart Receipt Auto-sign */}
              {formik.values.doctorDetails.receiptMakerAccess && (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Smart Receipt: Auto-Sign Receipts</h4>
                    <p className="text-[11px] text-primary-400 mt-0.5">
                      {!formik.values.doctorDetails.signatureImage
                        ? "Upload signature image to enable"
                        : "Automatically signs receipts when generating PDF"}
                    </p>
                  </div>
                  {formik.values.doctorDetails.signatureImage ? (
                    <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                      <input type="checkbox" className="sr-only peer" name="doctorDetails.autoReceiptSignature" checked={formik.values.doctorDetails.autoReceiptSignature} onChange={(e) => handleOnChange("autoReceiptSignature", e)} />
                      <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                    </label>
                  ) : (
                    <span className="flex-shrink-0"><CloseBg fill="#8E1010" height={20} width={20} /></span>
                  )}
                </div>
              )}

              {/* Auto-Stamp Receipts */}
              {formik.values.doctorDetails.receiptMakerAccess && (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Auto-Stamp Receipts</h4>
                    <p className="text-[11px] text-primary-400">Adds clinic stamp to receipts when generating PDF</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.autoStampReceipts" checked={formik.values.doctorDetails.autoStampReceipts} onChange={(e) => handleOnChange("autoStampReceipts", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
              )}

              {/* Show RegNo in Receipt Header */}
              {formik.values.doctorDetails.receiptMakerAccess && (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-[13px] font-medium text-black-lose">Show Registration number in header</h4>
                    <p className="text-[11px] text-primary-400 mt-0.5">Toggle visibility of registration number specifically at PDF header (does not affect stamp)</p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" name="doctorDetails.showRegNoInReceiptHeader" checked={formik.values.doctorDetails.showRegNoInReceiptHeader} onChange={(e) => handleOnChange("showRegNoInReceiptHeader", e)} />
                    <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                  </label>
                </div>
              )}

              {/* Access Controls Divider */}
              {formik.values.doctorDetails.receiptMakerAccess && (
                <div className="border-t border-primary-100 pt-3">
                  <p className="text-[11px] text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z" fill="#999" /></svg>
                    Access Controls (PIN-A restricted)
                  </p>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-[13px] font-medium text-black-lose">Services Page</h4>
                      <p className="text-[11px] text-primary-400">Show Services tab for PIN-A users when ON</p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                      <input type="checkbox" className="sr-only peer" name="doctorDetails.rcptServicesAccess" checked={formik.values.doctorDetails.rcptServicesAccess} onChange={(e) => handleOnChange("rcptServicesAccess", e)} />
                      <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-[13px] font-medium text-black-lose">List Page</h4>
                      <p className="text-[11px] text-primary-400">Show List tab for PIN-A users when ON</p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                      <input type="checkbox" className="sr-only peer" name="doctorDetails.rcptListAccess" checked={formik.values.doctorDetails.rcptListAccess} onChange={(e) => handleOnChange("rcptListAccess", e)} />
                      <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600"></div>
                    </label>
                  </div>
                  {/* Receipt Maker Preset Slider */}
                  <div className="mt-4 pt-3 border-t border-primary-50">
                    <p className="text-[11px] text-primary-400 mb-2">QUICK SETUP</p>
                    <div className="flex bg-primary-100 p-1 rounded-[8px] relative overflow-hidden">
                      <div
                        className="absolute top-1 bottom-1 w-[calc(33.33%-2px)] bg-white rounded-[6px] shadow-sm transition-all duration-300 ease-in-out z-0"
                        style={{
                          transform: `translateX(${getRcptAccessPreset() === 'doctor' ? '0%' : getRcptAccessPreset() === 'reception' ? '100%' : '200%'})`,
                          left: getRcptAccessPreset() === 'doctor' ? '4px' : getRcptAccessPreset() === 'reception' ? '2px' : '0px'
                        }}
                      ></div>
                      <button type="button" onClick={() => handleRcptAccessPreset('doctor')} className={`flex-1 text-center py-1.5 text-[11px] font-medium rounded-md transition-all z-10 ${getRcptAccessPreset() === "doctor" ? "text-primary-700 font-semibold" : "text-primary-500 hover:text-primary-600"}`}>Doctor Operated</button>
                      <button type="button" onClick={() => handleRcptAccessPreset('reception')} className={`flex-1 text-center py-1.5 text-[11px] font-medium rounded-md transition-all z-10 ${getRcptAccessPreset() === "reception" ? "text-primary-700 font-semibold" : "text-primary-500 hover:text-primary-600"}`}>Reception Operated</button>
                      <button type="button" onClick={() => handleRcptAccessPreset('custom')} className={`flex-1 text-center py-1.5 text-[11px] font-medium rounded-md transition-all z-10 ${getRcptAccessPreset() === "custom" ? "text-primary-700 font-semibold" : "text-primary-500 hover:text-primary-600"}`}>Custom</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ═══ PRESCRIPTION & RECEIPT DESIGN SETTINGS BLOCK ═══ */}
      <div className="bg-white border border-primary-200 rounded-[10px] overflow-hidden mb-3">
        <div className="bg-primary-50 px-4 py-2.5 border-b border-primary-200 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM7 13L11 17L17 7L15.59 5.59L11 10.17L8.41 7.59L7 13Z" fill="#513E3E" /></svg>
          <h3 className="text-[13px] font-semibold text-secondry-600 uppercase tracking-wide">Prescription &amp; Receipt Design Settings</h3>
        </div>
        <div className="p-4 space-y-6">
          {/* Row 1: Logo & Signature */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Custom Logo Card */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-1">Use Custom Logo</p>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 border-2 border-dashed border-primary-200 rounded-lg flex items-center justify-center bg-primary-50 overflow-hidden relative group">
                  {formik.values.doctorDetails.doctorLogo ? (
                    <img
                      src={typeof formik.values.doctorDetails.doctorLogo === 'string' ? formik.values.doctorDetails.doctorLogo : URL.createObjectURL(formik.values.doctorDetails.doctorLogo)}
                      alt="Logo Preview"
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="opacity-20"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6l4.5-6L14 17l2.5-3.5L18 19z" fill="#513E3E" /></svg>
                  )}
                  <input type="file" accept=".png,.jpeg,.jpg" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center justify-between p-2.5 bg-primary-50 rounded-lg border border-primary-100">
                      <div>
                        <h4 className="text-[12px] font-medium text-black-lose">Prescription</h4>
                        <p className="text-[10px] text-primary-400 mt-0.5">Show in Prescription</p>
                      </div>
                      <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          disabled={!formik.values.doctorDetails.doctorLogo}
                          name="doctorDetails.useLogo"
                          checked={formik.values.doctorDetails.useLogo}
                          onChange={(e) => handleOnChange("useLogo", e)}
                        />
                        <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600 disabled:opacity-50"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-primary-50 rounded-lg border border-primary-100">
                      <div>
                        <h4 className="text-[12px] font-medium text-black-lose">Receipt</h4>
                        <p className="text-[10px] text-primary-400 mt-0.5">Show in Receipt</p>
                      </div>
                      <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          disabled={!formik.values.doctorDetails.doctorLogo}
                          name="doctorDetails.useLogoInReceipt"
                          checked={formik.values.doctorDetails.useLogoInReceipt}
                          onChange={(e) => handleOnChange("useLogoInReceipt", e)}
                        />
                        <div className="relative w-7 h-4 bg-primary-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-success-600 disabled:opacity-50"></div>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" className="flex-1 text-[11px] h-8 rounded-full border border-primary-600 bg-white text-secondry-600 shadow-none">
                      <label className="cursor-pointer w-full h-full flex items-center justify-center">Change Logo<input type="file" accept=".png,.jpeg,.jpg" onChange={handleLogoUpload} className="hidden" /></label>
                    </Button>
                    {formik.values.doctorDetails.doctorLogo && (
                      <Button onClick={handleLogoDelete} type="button" className="flex-1 text-[11px] h-8 rounded-full border border-red-500 bg-white text-red-600 shadow-none">Remove</Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Manage Digital Signature Card */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-1">Manage Digital Signature</p>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 border-2 border-dashed border-primary-200 rounded-lg flex items-center justify-center bg-primary-50 overflow-hidden relative">
                  {formik.values.doctorDetails.signatureImage ? (
                    <img
                      src={typeof formik.values.doctorDetails.signatureImage === 'string' ? formik.values.doctorDetails.signatureImage : URL.createObjectURL(formik.values.doctorDetails.signatureImage)}
                      alt="Signature Preview"
                      className="w-full h-full object-contain p-2"
                    />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="opacity-20"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#513E3E" /></svg>
                  )}
                  <input type="file" name="doctorDetails.signatureImage" accept=".png,.jpeg" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="p-2.5 bg-primary-50 rounded-lg border border-primary-100 min-h-[54px] flex flex-col justify-center">
                    <span className="text-[10px] font-medium text-primary-400 uppercase tracking-wider mb-0.5">Active Signature</span>
                    {formik.values.doctorDetails.signatureImage ? (
                      <div className="flex items-center gap-1.5">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#4CAF50" /></svg>
                        <span className="text-[11px] text-success-600 font-semibold truncate max-w-[150px]">
                          {typeof formik.values.doctorDetails.signatureImage === 'string' && formik.values.doctorDetails.signatureImage.startsWith("http")
                            ? formik.values.doctorDetails.signatureImage.split("/").pop()
                            : formik.values.doctorDetails.signatureImage.name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-primary-400 italic">No signature uploaded</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" className="flex-1 text-[11px] h-8 rounded-full border border-primary-600 bg-white text-secondry-600 shadow-none relative">
                      <label className="cursor-pointer w-full h-full flex items-center justify-center">Change Signature<input type="file" accept=".png,.jpeg" onChange={handleImageUpload} className="hidden" /></label>
                    </Button>
                    {formik.values.doctorDetails.signatureImage && (
                      <Button onClick={handleDelete} type="button" className="flex-1 text-[11px] h-8 rounded-full border border-red-500 bg-white text-red-600 shadow-none">Remove</Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Theme Settings */}
          <div className="pt-2">
            <p className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-4">Prescription &amp; Receipt Theme</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {themes.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => handleThemeSelect(t)}
                  className={`flex flex-col items-center gap-2 p-2.5 rounded-xl border-2 transition-all ${formik.values.doctorDetails.prescriptionPrimaryColor === t.primary ? 'border-primary-600 bg-primary-50 shadow-sm scale-[1.02]' : 'border-transparent bg-gray-50 hover:bg-gray-100'}`}
                >
                  <div className="flex flex-col w-12 h-10 rounded-lg border-2 border-white shadow-sm overflow-hidden flex-shrink-0">
                    <div style={{ backgroundColor: t.primary }} className="flex-1"></div>
                    <div style={{ backgroundColor: t.secondary }} className="flex-1"></div>
                  </div>
                  <span className="text-[11px] font-medium text-secondry-600">{t.name}</span>
                </button>
              ))}

              {/* Custom Palette Block */}
              <div
                className={`flex flex-col items-center gap-2 p-2.5 rounded-xl border-2 transition-all ${!themes.some(t => t.primary === formik.values.doctorDetails.prescriptionPrimaryColor) ? 'border-primary-600 bg-primary-50 shadow-sm scale-[1.02]' : 'border-dashed border-primary-200 bg-white hover:bg-primary-50'}`}
              >
                <div className="flex flex-col w-12 h-10 rounded-lg border-2 border-white shadow-sm overflow-hidden flex-shrink-0 relative">
                  {/* Primary Color Picker */}
                  <div style={{ backgroundColor: formik.values.doctorDetails.prescriptionPrimaryColor }} className="flex-1 relative group/color">
                    <input
                      type="color"
                      value={formik.values.doctorDetails.prescriptionPrimaryColor}
                      onChange={(e) => {
                        formik.setFieldValue("doctorDetails.prescriptionPrimaryColor", e.target.value);
                        handleSubmit({ prescriptionPrimaryColor: e.target.value });
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full scale-150"
                      title="Change Primary Color"
                    />
                  </div>
                  {/* Secondary Color Picker */}
                  <div style={{ backgroundColor: formik.values.doctorDetails.prescriptionSecondaryColor }} className="flex-1 relative group/color">
                    <input
                      type="color"
                      value={formik.values.doctorDetails.prescriptionSecondaryColor}
                      onChange={(e) => {
                        formik.setFieldValue("doctorDetails.prescriptionSecondaryColor", e.target.value);
                        handleSubmit({ prescriptionSecondaryColor: e.target.value });
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full scale-150"
                      title="Change Secondary Color"
                    />
                  </div>
                </div>
                <span className="text-[11px] font-medium text-secondry-600">Custom</span>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* ═══ RAPID SCAN ORDER SETTINGS BLOCK ═══ */}
      <div className="bg-white border border-primary-200 rounded-[10px] overflow-hidden mb-3">
        <div className="bg-primary-50 px-4 py-2.5 border-b border-primary-200 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L12 10 18 10 12 22 12 14 6 14 12 2Z" fill="#513E3E" stroke="#513E3E" strokeWidth="2" strokeLinejoin="round" /></svg>
          <h3 className="text-[13px] font-semibold text-secondry-600 uppercase tracking-wide">Rapid Scan Order Settings</h3>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-100 max-w-md">
            <div>
              <h4 className="text-[13px] font-medium text-black-lose">Enable Rapid Scan Order</h4>
              <p className="text-[11px] text-primary-400 mt-0.5">Allow generating orders from our Intelligent Prescription Scanner.</p>
            </div>
            <label className="inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                name="doctorDetails.rapidScanAccess"
                checked={formik.values.doctorDetails.rapidScanAccess !== false}
                onChange={(e) => handleOnChange("rapidScanAccess", e)}
              />
              <div className="relative w-9 h-5 bg-primary-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-success-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* ═══ MAIN INFO BLOCK ═══ */}
      <div className="bg-white border border-primary-200 rounded-[10px] overflow-hidden mb-3">

        {/* Helper: render a label+value field */}
        {(() => {
          const Field = ({ label, value }) => (
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-primary-400 uppercase tracking-wider">{label}</span>
              <span className="text-[13px] text-secondry-600 font-medium whitespace-pre-wrap">{value || <span className="text-primary-300 italic text-[12px]">—</span>}</span>
            </div>
          );

          const Section = ({ title, children }) => (
            <div className="px-4 py-3 border-b border-primary-100 last:border-b-0">
              <p className="text-[10px] font-semibold text-primary-400 uppercase tracking-widest mb-2.5">{title}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                {children}
              </div>
            </div>
          );

          const v = formik.values;

          return (
            <>
              {/* Primary */}
              <Section title="Primary">
                <Field label="Doctor ID" value={v.doctorDetails?.DID} />
                <Field label="Contact Number" value={v.doctorDetails?.contactNumber} />
              </Section>

              {/* Compliances */}
              <Section title="Compliances">
                <Field label="GST Number" value={v.compliances?.gstNumber} />
                <Field label="Registration Number" value={v.compliances?.registrationNumber} />
              </Section>

              {/* Category */}
              <Section title="Category">
                <Field label="Category" value={v.accountCategory?.category} />
              </Section>

              {/* Personal Info + Clinic Info side by side on wider screens */}
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-primary-100 border-b border-primary-100">
                <div className="px-4 py-3">
                  <p className="text-[10px] font-semibold text-primary-400 uppercase tracking-widest mb-2.5">Personal Info</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <Field label="Name" value={v.personalInfo?.name} />
                    <Field label="Surname" value={v.personalInfo?.surname} />
                    <Field label="Qualification / Specialisation" value={v.personalInfo?.qualificationSpecialisation} />
                    <Field label="Experience (Years)" value={v.personalInfo?.experience} />
                    <div className="col-span-2"><Field label="About" value={v.personalInfo?.about1} /></div>
                  </div>
                </div>
                <div className="px-4 py-3">
                  <p className="text-[10px] font-semibold text-primary-400 uppercase tracking-widest mb-2.5">Clinic Info</p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <Field label="Clinic Name" value={v.personalInfo?.clinicName} />
                    <Field label="Tagline" value={v.personalInfo?.tagline} />
                    <Field label="Operator Name" value={v.personalInfo?.operatorName} />
                    <Field label="Manager Name" value={v.personalInfo?.managerName} />
                    <div className="col-span-2"><Field label="About" value={v.personalInfo?.about2} /></div>
                  </div>
                </div>
              </div>

              {/* Clinic Address */}
              <Section title="Clinic Address">
                <Field label="Premises No., Floor" value={v.clinicAddress?.premisesNo} />
                <Field label="Premises Name" value={v.clinicAddress?.premisesName} />
                <Field label="Landmark" value={v.clinicAddress?.landmark} />
                <Field label="Area / Road" value={v.clinicAddress?.areaRoad} />
                <Field label="City" value={v.clinicAddress?.city} />
                <Field label="Pin Code" value={v.clinicAddress?.pinCode} />
                <Field label="State" value={v.clinicAddress?.state} />
                <Field label="Clinic Contact" value={v.clinicAddress?.clinicContactNumber} />
              </Section>

              {/* Email */}
              <Section title="Email">
                <Field label="Doctor Email" value={v.emailInfo?.doctorEmail} />
                <Field label="Clinic Email" value={v.emailInfo?.clinicEmail} />
              </Section>

              {/* Payment */}
              <div className="px-4 py-3 border-b border-primary-100">
                <p className="text-[10px] font-semibold text-primary-400 uppercase tracking-widest mb-2.5">Payment</p>
                {doctorById?.paymentDetail?.bankAccountNumber && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 mb-2">
                    <div className="col-span-4">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full mb-2">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M2 10h20v2H2zm0 6h20v2H2zm18-14H4C2.9 2 2 2.9 2 4v2h20V4c0-1.1-.9-2-2-2z" fill="#513E3E" /></svg>
                        Bank Account
                      </span>
                    </div>
                    <Field label="Account Number" value={v.paymentDetails?.bankAccountNumber} />
                    <Field label="Account Name" value={v.paymentDetails?.accountName} />
                    <Field label="Bank Name" value={v.paymentDetails?.bankName} />
                    <Field label="IFSC Code" value={v.paymentDetails?.ifscName} />
                  </div>
                )}
                {doctorById?.paymentDetail?.upiId && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                    <div className="col-span-4">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full mb-2">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="#513E3E" /></svg>
                        UPI Account
                      </span>
                    </div>
                    <Field label="UPI ID" value={v.paymentDetails?.upiId} />
                  </div>
                )}
                {!doctorById?.paymentDetail?.bankAccountNumber && !doctorById?.paymentDetail?.upiId && (
                  <span className="text-[12px] text-primary-300 italic">No payment details added</span>
                )}
              </div>

              {/* Account Created */}
              <div className="px-4 py-2.5 bg-primary-50 flex items-center justify-end">
                <span className="text-[11px] text-primary-400">Account created on {formattedDate}</span>
              </div>
            </>
          );
        })()}
      </div>


    </div>
  );
};

export default Profile;
